import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAlerts,
  setErrorAlert,
  setSuccessAlert,
} from "../../../store/alert/alertSlice";
import { postDataAPI } from "../../../utils/fetchData";
import Check from "../../../svg/Check";
import dizeeLogo from "../../../assets/images/dizee logo.svg";
import TypeWriter from "../../../components/OnBoard/Typewriter";
import styled from "styled-components";
import AccessibilityWidget from "../../../components/AccessibilityWidget";
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
`;

const PlanOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 29px;
  font-size: 12px;
  color: white;
  margin: 2px 0;
  width: 280px;
`;

const PlanOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 18px;
  line-height: 18px;
  gap: 35px;
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  display: flex;
  align-items: center;
  position: relative;
`;

const ActionButton = styled.div`
  font-size: 12px;
  color: white;
  cursor: pointer;
  height: 18px;
  line-height: 18px;
  position: relative;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

// Role display names for team members
const ROLE_DISPLAY_NAMES = {
  'manager': 'Manager',
  'support': 'Support',
  'agent': 'Agent',
  'label': 'Label',
  'publisher': 'Publisher',
  'business-manager': 'Business Manager'
};

const comment = {
  basic: "Full-featured team member with customizable permissions. Manage the artist's career, handle business affairs, and coordinate with other team members. ($35/mo)",
  pro: "Full-featured team member with customizable permissions, billed annually for savings. Manage the artist's career with all advanced features. ($25/mo annual)",
};

const Step15 = ({ setStep, setUser, user }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  const handleSkipTrial = async () => {
    dispatch(clearAlerts());
    try {
      const url = new URL(window.location.href);
      const emailParam = url.searchParams.get("email");

      const trialData = {
        email: emailParam || userData?.email,
        phone: userData?.phone,
        trialStartDate: new Date().toISOString(),
        trialStatus: "active",
      };

      // API call commented out for now - will be enabled later
      // const response = await postDataAPI("auth/start-trial", trialData);

      dispatch(setSuccessAlert("3-day trial started"));
      setStep(16); // Go to artist selection page for team members
    } catch (error) {
      console.error("Trial start failed:", error);
      dispatch(setErrorAlert("Failed to start trial. Please try again."));
    }
  };

  const handleSupportContinue = async () => {
    // Support accounts are free, pre-fetch invitations then go to artist selection
    setUser({ ...user, teamMemberPlan: 'support' });
    
    // Pre-fetch team invitations so step16 has data ready
    try {
      const url = new URL(window.location.href);
      const emailParam = url.searchParams.get("email");
      const userParam = url.searchParams.get('user');
      
      let teamMemberEmail = emailParam;
      if (userParam) {
        try {
          const parsedUserParam = JSON.parse(userParam);
          teamMemberEmail = parsedUserParam.email;
        } catch (e) {
          // Use emailParam as fallback
        }
      }
      
      if (teamMemberEmail) {
        const response = await postDataAPI("team/invited-artists", {
          teamMemberEmail: teamMemberEmail
        });
        
        if (response.data?.status === 200) {
          // Store the pre-fetched invitations in user state
          setUser(prevUser => ({ 
            ...prevUser, 
            teamMemberPlan: 'support',
            availableArtists: response.data.data || []
          }));
        }
      }
    } catch (error) {
      console.error("Error pre-fetching invitations:", error);
      // Continue anyway, step16 will handle the empty state
    }
    
    setStep(16);
  };

  // Team member payment handler
  const handleTeamMemberPayment = async (plan) => {
    if (loading) return;
    
    setLoading(true);
    dispatch(clearAlerts());
    
    try {
      const persistedData = localStorage.getItem("persist:root");
      const parsedData = JSON.parse(persistedData);
      let userData = JSON.parse(parsedData.user);
      let phone = userData.phone;
      const url = new URL(window.location.href);
      const emailParam = url.searchParams.get("email");
      let email = emailParam;
      let userDataState = JSON.stringify(user);

      console.log("team member plan", plan);
      
      // Call team member payment API
      const response = await postDataAPI("team/stripe-payment", {
        plan: plan,
        user: userDataState,
        email: email,
        phone: phone,
        userType: 'team-member',
        role: user.professionRole
      });

      if (response.data?.status === 200 && response.data?.data?.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.data.url;
      } else {
        throw new Error(response.data?.message || 'Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      dispatch(setErrorAlert(error.message || 'Payment failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && user?.teamMemberPlan && !loading) {
      handleTeamMemberPayment(user?.teamMemberPlan);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [user.teamMemberPlan, loading]);

  const getPlanText = () => {
    if (user?.teamMemberPlan === 'basic') {
      return comment.basic;
    } else if (user?.teamMemberPlan === 'pro') {
      return comment.pro;
    }
    return `Choose the plan that best fits your needs as a ${ROLE_DISPLAY_NAMES[user?.professionRole] || 'team member'}.`;
  };

  const getRoleDisplayName = () => {
    return ROLE_DISPLAY_NAMES[user?.professionRole] || 'Team Member';
  };

  return (
    <Container>
      <FormSection>
        {user?.professionRole === 'support' ? (
          <>
            <TypeWriter text={`Welcome, ${getRoleDisplayName()}!`} speed={45} />
            <div className="text-white text-[12px] leading-[1.4] mt-[20px] w-[280px]">
              Support accounts have view-only access to selected apps and are completely free. 
              You'll be able to help artists manage their Dizee account with limited permissions.
            </div>
            <ButtonContainer>
              <ActionButton className="mr-[43px]" onClick={handleSupportContinue}>
                Continue
              </ActionButton>
              <ActionButton className="cursor-pointer" onClick={() => setStep(2)}>Go back</ActionButton>
            </ButtonContainer>
          </>
        ) : (
          <>
            <TypeWriter text={`Select your ${getRoleDisplayName().toLowerCase()} plan`} speed={45} />
            <PlanOptions>
              <PlanOption>
                <div
                  className="cursor-pointer flex items-center"
                  onClick={() => {
                    setUser({ ...user, teamMemberPlan: 'basic' });
                  }}
                >
                  <div>Basic</div>
                  <div className="w-[18px] h-[12px] ml-2">
                    {user?.teamMemberPlan === 'basic' && (
                      <Check className="h-[12px] w-[18px]" />
                    )}
                  </div>
                </div>
                <div>$35/month</div>
              </PlanOption>

              <PlanOption>
                <div
                  className="cursor-pointer flex items-center"
                  onClick={() => {
                    setUser({ ...user, teamMemberPlan: 'pro' });
                  }}
                >
                  <div>Pro</div>
                  <div className="w-[18px] h-[12px] ml-2">
                    {user?.teamMemberPlan === 'pro' && (
                      <Check className="h-[12px] w-[18px]" />
                    )}
                  </div>
                </div>
                <div>$25/month (annual)</div>
              </PlanOption>
            </PlanOptions>

            <ButtonContainer>
              {/* Payment functionality temporarily hidden - will be enabled later */}
              {/* 
              <ActionButton
                onClick={() => user?.teamMemberPlan && !loading && handleTeamMemberPayment(user?.teamMemberPlan)}
                className={`mr-[43px] ${user?.teamMemberPlan && !loading ? "font-default" : "font-gray"}`}
              >
                {loading ? "Processing..." : "Continue to Payment"}
              </ActionButton>
              */}
              <ActionButton className="mr-[43px]" onClick={handleSkipTrial}>Skip</ActionButton>
              <ActionButton className="cursor-pointer" onClick={() => setStep(2)}>Go back</ActionButton>
            </ButtonContainer>
          </>
        )}
      </FormSection>
      
      <Footer>
        <LogoImage src={dizeeLogo} alt="DIZEE" />
        <FooterText>
          {user?.professionRole === 'support' 
            ? "Support accounts are free and provide view-only access to help artists manage their presence on Dizee."
            : "Skip now and try DIZEE free for 3 days"
          }
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step15; 